import { ButtonBox } from '@/components/UI/Box';
import { Box, Button, Grid, Typography } from '@mui/material';
import { ChangeEvent, useState } from 'react';

import { PaymentStatus, StatusCode } from '../../../../api/enumerations';
import {
  addAttachment,
  changePaymentStatus,
  deleteAttachment,
  getHivePayment,
} from '../../../../api/financial';
import { HivePaymentData } from '../../../../api/financial/types';
import { CustomTextField } from '../../../../components/CustomInput';
import {
  StyledLinearProgress,
  WhiteCircularProgress,
} from '../../../../components/UI/Typography';
import { Constants } from '../../../../constants/finance';
import {
  IconAttachFileMS,
  IconCloseMS,
  IconDeleteMS,
  IconDownloadMS,
  IconFolderOpenMS,
  IconPaymentsMS,
} from '../../../../constants/icons';
import { formatOnlyDate, validateFiles } from '../../../../helpers';
import useErrorMessage from '../../../../hooks/useErrorMessage';
import useGeneral from '../../../../hooks/useGeneral';
import useSnackbar from '../../../../hooks/useSnackbar';
import { ActionButton } from '../../styles';
import CancelDialog from '../CancelDialog';
import { PaymentHeader } from '../PaymentHeader';
import { StatusStyle } from '../Table/PaymentActionButton';
import {
  AttachFileButton,
  CancelButton,
  CloseButton,
  FileContainer,
  FileList,
  FileNameTypography,
  InputBox,
  SectionContainer,
  SectionTitle,
  StyledDialog,
  StyledIcon,
  SubmitBox,
  SubmitButton,
  TopBox,
} from './styles';

export interface RegisterPaymentDialogProps {
  paymentId: number;
  styles: StatusStyle;
  updateData: () => void;
}

export default function RegisterPaymentDialog({
  paymentId,
  styles,
  updateData,
}: RegisterPaymentDialogProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [paymentData, setPaymentData] = useState<HivePaymentData | undefined>();
  const [fileName, setFileName] = useState('');
  const [fileAttached, setFileAttached] = useState<File | string>();
  const [reason, setReason] = useState('');
  const [loadingData, setLoadingData] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [loadingCancellation, setLoadingCancellation] = useState(false);

  const { getErrorMessage } = useErrorMessage();
  const { handleSnackbar } = useSnackbar();
  const { handleDownloadFile } = useGeneral();

  const resetInputs = (): void => {
    setFileName('');
    setFileAttached(undefined);
    setReason('');
  };

  const getDataCallback = async (): Promise<void> => {
    try {
      const response = await getHivePayment(paymentId);

      if (response.detail.description)
        throw new Error(response.detail.description);

      if (!response.data) throw new Error('Algo deu errado, tente novamente.');

      setPaymentData(response.data);
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    } finally {
      setLoadingData(false);
    }
  };

  const handleOpen = async (): Promise<void> => {
    setOpen(true);
    await getDataCallback();
  };

  const handleClose = (): void => {
    setOpen(false);
    resetInputs();
  };

  const handleSubmitFile = async (): Promise<void> => {
    if (!fileAttached) {
      handleSnackbar('Adicione um arquivo!', true);
      return;
    }

    setLoading(true);

    const formData = new FormData();
    formData.append('file', fileAttached);
    formData.append('kind', 'payment');

    try {
      const response = await addAttachment(paymentId, formData);

      if (response.detail.description)
        throw new Error(response.detail.description);

      if (response.detail.status_code !== StatusCode.OK)
        throw new Error('Algo deu errado, tente novamente.');

      handleSnackbar('Arquivo adicionado com sucesso!', false);
      await getDataCallback();
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    } finally {
      setLoading(false);
      resetInputs();
    }
  };

  const handleStatus = async (): Promise<void> => {
    setLoadingStatus(true);
    try {
      const response = await changePaymentStatus(paymentId, {
        status: PaymentStatus.PAID,
      });

      if (response.detail.description)
        throw new Error(response.detail.description);

      if (response.detail.status_code !== StatusCode.OK)
        throw new Error('Algo deu errado, tente novamente.');

      handleSnackbar('Pagamento confirmado com sucesso!', false);
      updateData();
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    } finally {
      setLoadingStatus(false);
    }
  };

  const handleDeleteFile = async (fileId: number): Promise<void> => {
    setLoadingDelete(true);
    try {
      const response = await deleteAttachment(paymentId, fileId);

      if (response.detail.description)
        throw new Error(response.detail.description);

      if (response.detail.status_code !== StatusCode.OK)
        throw new Error('Algo deu errado, tente novamente.');

      handleSnackbar('Arquivo deletado com sucesso!', false);
      await getDataCallback();
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    } finally {
      setLoadingDelete(false);
    }
  };

  const handleCancellation = async (): Promise<void> => {
    if (!reason) {
      handleSnackbar('Informe o motivo da solicitação do cancelamento', true);
      return;
    }
    try {
      setLoadingCancellation(true);

      const response = await changePaymentStatus(paymentId, {
        status: PaymentStatus.CANCELLED,
        canceled_refunded_reason: reason,
      });

      if (response.detail.description)
        throw new Error(response.detail.description);

      if (response.detail.status_code !== StatusCode.OK)
        throw new Error('Algo deu errado, tente novamente.');

      handleSnackbar(`O pagamento foi cancelado com sucesso!`, false);
      updateData();
      handleClose();
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    } finally {
      setLoadingCancellation(false);
    }
  };

  const handleFileUpload = async (
    e: ChangeEvent<HTMLInputElement>
  ): Promise<void> => {
    if (!e.target.files?.item(0) || !validateFiles(e.target.files[0].type)) {
      handleSnackbar('Formato incorreto, selecione uma imagem ou pdf', true);
      return;
    }

    const file = e.target.files[0];
    setFileName(file.name);
    setFileAttached(file);
  };

  return (
    <>
      <ActionButton
        bgcolor={styles.bg}
        textcolor={styles.color}
        onClick={handleOpen}
      >
        {Constants.pay}
      </ActionButton>
      <StyledDialog open={open} onClose={handleClose}>
        <Box>
          {loadingData ? (
            <StyledLinearProgress />
          ) : (
            <Box>
              <TopBox>
                <CloseButton onClick={handleClose}>{IconCloseMS}</CloseButton>
              </TopBox>
              <PaymentHeader paymentData={paymentData} />
              <SectionContainer>
                <SectionTitle>
                  {IconPaymentsMS}
                  {Constants.paymentInfo}
                </SectionTitle>
                <Grid container spacing={2} mb={2}>
                  <Grid item xs={4}>
                    {Constants.licensed}
                    <Typography fontWeight={600}>
                      {paymentData?.licensed_company_name}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    {Constants.notice}
                    <Typography fontWeight={600}>
                      {paymentData?.public_notice_name}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    {Constants.createdAt}
                    <Typography fontWeight={600}>
                      {paymentData && formatOnlyDate(paymentData.created_at)}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    {Constants.approvedAt}
                    <Typography fontWeight={600}>
                      {paymentData?.approved_at &&
                        formatOnlyDate(paymentData.approved_at)}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    {Constants.invoicedAt}
                    <Typography fontWeight={600}>
                      {paymentData?.invoiced_at &&
                        formatOnlyDate(paymentData.invoiced_at)}
                    </Typography>
                  </Grid>
                </Grid>
              </SectionContainer>
              <SectionContainer>
                <SectionTitle>
                  {IconFolderOpenMS}
                  {Constants.files}
                </SectionTitle>
                <InputBox>
                  <CustomTextField
                    disabled
                    id="file-name"
                    label="arquivo"
                    value={fileName}
                    setValue={setFileName}
                  />
                  <label htmlFor="uploadRegisterFiles">
                    <input
                      accept="image/png,image/jpeg,image/jpg,application/pdf"
                      id="uploadRegisterFiles"
                      type="file"
                      style={{ display: 'none' }}
                      onChange={async (e: ChangeEvent<HTMLInputElement>) =>
                        handleFileUpload(e)
                      }
                    />
                    <AttachFileButton component="span">
                      {Constants.selectFile}
                    </AttachFileButton>
                  </label>
                </InputBox>
                <SubmitBox>
                  <CancelButton onClick={resetInputs}>
                    {Constants.cancel}
                  </CancelButton>
                  <SubmitButton disabled={loading} onClick={handleSubmitFile}>
                    {loading ? (
                      <WhiteCircularProgress size={22} />
                    ) : (
                      Constants.add
                    )}
                  </SubmitButton>
                </SubmitBox>
                <FileList>
                  {paymentData?.files &&
                    paymentData.files.length > 0 &&
                    paymentData.files.map((file) => (
                      <FileContainer container key={file.id}>
                        <Box>
                          <FileNameTypography>
                            {IconAttachFileMS}
                            {file.filename}
                          </FileNameTypography>
                        </Box>
                        <Box display="flex" gap="8px">
                          <StyledIcon
                            onClick={() =>
                              file.file &&
                              handleDownloadFile(file.file, file.filename)
                            }
                          >
                            {IconDownloadMS}
                          </StyledIcon>
                          <StyledIcon
                            disabled={loadingDelete}
                            onClick={() => handleDeleteFile(file.id)}
                          >
                            {IconDeleteMS}
                          </StyledIcon>
                        </Box>
                      </FileContainer>
                    ))}
                </FileList>
              </SectionContainer>
              <ButtonBox margin="28px 8px" gap={4}>
                <Button
                  disabled={loadingStatus}
                  sx={{ width: '100%' }}
                  onClick={handleStatus}
                >
                  {loadingStatus ? (
                    <WhiteCircularProgress size={22} />
                  ) : (
                    Constants.confirmPayment
                  )}
                </Button>
                <CancelDialog
                  loading={loadingCancellation}
                  reason={reason}
                  setReason={setReason}
                  onSubmit={handleCancellation}
                />
              </ButtonBox>
            </Box>
          )}
        </Box>
      </StyledDialog>
    </>
  );
}
